import { logger } from "./logger";

/**
 * SECURITY: AI Assistant State Management Utilities
 * 
 * Ensures AI chat data is strictly bound to authenticated user sessions
 * and is never visible after logout or across accounts.
 */

/**
 * Clear all AI assistant state for a specific user
 * @param {string} userId - User ID to clear state for
 */
export const clearUserAssistantState = (userId, dispatch = null) => {
  try {
    // Clear Redux assistant state if dispatch provided
    if (dispatch) {
      const { clearUserThread, clearAllThreads } = require('../redux/dashboard/reducers/assistantSlice');
      if (userId) {
        dispatch(clearUserThread({ userId }));
      } else {
        dispatch(clearAllThreads());
      }
    }

    // Clear all career chat localStorage entries for this user
    if (userId) {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(`career-chat-${userId}-`)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } else {
      // Clear all career chat entries if no userId
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('career-chat-')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }

    logger.log('[assistantSecurity] Cleared assistant state for user:', userId || 'all');
  } catch (error) {
    logger.error('[assistantSecurity] Error clearing assistant state:', error);
  }
};

/**
 * Validate that current userId matches stored assistant state
 * @param {string} currentUserId - Current authenticated user ID
 * @returns {boolean} True if state is valid, false if user mismatch detected
 */
export const validateAssistantStateOwnership = (currentUserId) => {
  try {
    const storedUserId = localStorage.getItem('userId');
    
    if (!currentUserId || !storedUserId) {
      return false;
    }

    if (currentUserId !== storedUserId) {
      // User mismatch - clear all assistant state
      clearUserAssistantState(storedUserId);
      return false;
    }

    return true;
  } catch (error) {
    logger.error('[assistantSecurity] Error validating state ownership:', error);
    return false;
  }
};

/**
 * Get user-bound storage key for career chat
 * @param {string} userId - User ID
 * @param {string} companyId - Company ID
 * @param {string} pageType - Page type
 * @param {string} departmentId - Department ID (optional)
 * @param {string} jobId - Job ID (optional)
 * @returns {string} Storage key
 */
export const getCareerChatStorageKey = (userId, companyId, pageType, departmentId = null, jobId = null) => {
  const currentUserId = userId || localStorage.getItem('userId') || 'anonymous';
  return `career-chat-${currentUserId}-${companyId || 'default'}-${pageType}-${departmentId || ''}-${jobId || ''}`;
};

/**
 * Clear all assistant-related polling intervals and listeners
 * This should be called on logout to prevent memory leaks
 */
export const clearAssistantPolling = () => {
  // Clear any running intervals (stored in window for cleanup)
  if (typeof window !== 'undefined') {
    if (window.__assistantPollingIntervals) {
      window.__assistantPollingIntervals.forEach(intervalId => {
        clearInterval(intervalId);
      });
      window.__assistantPollingIntervals = [];
    }

    if (window.__assistantAbortControllers) {
      window.__assistantAbortControllers.forEach(controller => {
        controller.abort();
      });
      window.__assistantAbortControllers = [];
    }
  }
};

/**
 * Complete assistant state cleanup on logout
 * @param {string} userId - User ID being logged out
 * @param {Function} dispatch - Redux dispatch function (optional)
 */
export const performAssistantLogout = (userId, dispatch = null) => {
  logger.log('[assistantSecurity] Performing assistant logout cleanup for user:', userId);
  
  // Clear all state
  clearUserAssistantState(userId, dispatch);
  
  // Clear polling/intervals
  clearAssistantPolling();
  
  logger.log('[assistantSecurity] Assistant logout cleanup completed');
};

